import { useEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { observer } from 'mobx-react-lite'
import { Dropdown, Input, type InputRef } from 'antd'
import { MdClose, MdOutlineSearch } from 'react-icons/md'
import { LoadingOutlined } from '@ant-design/icons'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import type { SearchHit } from '@/Ship/Tasks/SearchResourcesTask'
import { getFrontendResourceKey, getResourceDisplayName } from '@/Ship/Helpers/resourceMapping'
import { useClickOutside } from '@/Ship/Hooks/useClickOutside'
import { SearchStore } from '@/Ship/Stores/SearchStore'

import styles from './Search.module.css'

/**
 * Public alias for the search-hit shape (kept for backwards-compat with
 * external consumers that already import `ISearchResult` from this module).
 */
export type ISearchResult = SearchHit

export interface ISearchProps {
    /** Ширина інпута пошуку (у фокусі розширюється в 1.3 раза) */
    width?: number
    /** Placeholder для інпута */
    placeholder?: string
    /** Затримка для debounce (мс) */
    debounceDelay?: number
}

/** Чи фокус зараз у полі вводу: тоді гарячу клавішу «/» не перехоплюємо */
function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false
    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

/** Підсвічує збіг запиту в назві результату */
function highlight(text: string, query: string) {
    const q = query.trim()
    if (!q) return text
    const at = text.toLowerCase().indexOf(q.toLowerCase())
    if (at < 0) return text
    return (
        <>
            {text.slice(0, at)}
            <mark className={styles['mark']}>{text.slice(at, at + q.length)}</mark>
            {text.slice(at + q.length)}
        </>
    )
}

/**
 * Глобальний пошук у шапці: список результатів, згрупований за ресурсом,
 * навігація стрілками, Enter відкриває картку, «/» фокусує поле з будь-якого місця.
 */
export const Search = observer(function Search({
    width = 240,
    placeholder = 'Пошук',
    debounceDelay = 800,
}: ISearchProps) {
    const [store] = useState(() => new SearchStore(debounceDelay))
    const [focused, setFocused] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<InputRef>(null)
    const activeItemRef = useRef<HTMLButtonElement>(null)

    useClickOutside(rootRef, () => store.setOpen(false))

    // Cancel pending debounce timer on unmount.
    useEffect(() => () => store.cancelDebounce(), [store])

    // «/» з будь-якого місця сторінки ставить курсор у пошук
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return
            e.preventDefault()
            inputRef.current?.focus()
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [])

    // Виділений з клавіатури результат тримаємо у видимій частині списку
    useEffect(() => {
        activeItemRef.current?.scrollIntoView({ block: 'nearest' })
    }, [store.activeIndex])

    const openResult = (result: ISearchResult) => {
        const frontendKey = getFrontendResourceKey(result.resource)
        if (!frontendKey) return
        store.clear()
        inputRef.current?.blur()
        OpenResourceCardTask({
            resource: frontendKey,
            primaryFieldValue: result.uuid,
            mode: 'view',
        })
    }

    const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
        switch (e.key) {
            case 'ArrowDown':
            case 'ArrowUp':
                if (store.isOpen && store.results.length) {
                    e.preventDefault()
                    store.moveActive(e.key === 'ArrowDown' ? 1 : -1)
                }
                break
            case 'Enter':
                if (store.activeResult) openResult(store.activeResult)
                else store.submitNow()
                break
            case 'Escape':
                // Спершу закриваємо список, потім чистимо поле, потім віддаємо фокус
                if (store.isOpen) store.setOpen(false)
                else if (store.searchValue) store.clear()
                else inputRef.current?.blur()
                break
        }
    }

    const query = store.searchValue.trim()
    const showClear = store.searchValue.length > 0 && !store.isLoading

    const dropdownContent = (
        <div className={styles['panel']} role="listbox" aria-label="Результати пошуку">
            {store.results.length > 0 ? (
                <>
                    <div className={styles['panelHeader']}>
                        <span>Знайдено: {store.results.length}</span>
                        <span className={styles['panelHint']}>
                            <kbd>↑</kbd>
                            <kbd>↓</kbd> вибрати · <kbd>Enter</kbd> відкрити
                        </span>
                    </div>
                    {store.groupedResults.map((group) => (
                        <div key={group.resource} className={styles['group']}>
                            <div className={styles['groupTitle']}>{getResourceDisplayName(group.resource)}</div>
                            {group.items.map(({ hit, index }) => {
                                const active = index === store.activeIndex
                                return (
                                    <button
                                        type="button"
                                        role="option"
                                        aria-selected={active}
                                        key={`${hit.resource}-${hit.uuid}`}
                                        ref={active ? activeItemRef : null}
                                        className={`${styles['item']}${active ? ` ${styles['itemActive']}` : ''}`}
                                        onMouseEnter={() => store.setActiveIndex(index)}
                                        onMouseDown={(e) => {
                                            // mousedown, а не click: інакше blur інпута закриє список раніше
                                            e.preventDefault()
                                            openResult(hit)
                                        }}
                                    >
                                        <span className={styles['itemMark']} aria-hidden="true">
                                            {getResourceDisplayName(hit.resource).charAt(0)}
                                        </span>
                                        <span className={styles['itemTitle']}>{highlight(hit.title, query)}</span>
                                    </button>
                                )
                            })}
                        </div>
                    ))}
                </>
            ) : (
                <div className={styles['empty']}>
                    <MdOutlineSearch className={styles['emptyIcon']} />
                    <div className={styles['emptyTitle']}>Нічого не знайдено за «{store.lastQuery}»</div>
                    <div className={styles['emptyHint']}>Спробуйте інше написання або коротший запит</div>
                </div>
            )}
        </div>
    )

    return (
        <div ref={rootRef} className={`${styles['search']}${focused ? ` ${styles['focused']}` : ''}`}>
            <Dropdown
                open={store.isOpen}
                popupRender={() => dropdownContent}
                trigger={[]}
                placement="bottomRight"
                classNames={{ root: styles['dropdown'] ?? '' }}
                destroyOnHidden
            >
                <div
                    style={
                        {
                            '--search-width': `${width}px`,
                            '--search-width-focus': `${Math.round(width * 1.3)}px`,
                        } as CSSProperties
                    }
                    className={styles['field']}
                >
                    <Input
                        ref={inputRef}
                        className={styles['input']}
                        placeholder={placeholder}
                        value={store.searchValue}
                        onChange={(e) => store.setSearchValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onFocus={() => {
                            setFocused(true)
                            store.onFocus()
                        }}
                        onBlur={() => setFocused(false)}
                        autoComplete="off"
                        spellCheck={false}
                        role="combobox"
                        aria-expanded={store.isOpen}
                        aria-autocomplete="list"
                        prefix={<MdOutlineSearch className={styles['prefixIcon']} />}
                        suffix={
                            store.isLoading ? (
                                <LoadingOutlined className={styles['spinner']} spin />
                            ) : showClear ? (
                                <button
                                    type="button"
                                    className={styles['clear']}
                                    aria-label="Очистити пошук"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        store.clear()
                                        inputRef.current?.focus()
                                    }}
                                >
                                    <MdClose />
                                </button>
                            ) : (
                                <kbd className={styles['kbd']} aria-hidden="true">
                                    /
                                </kbd>
                            )
                        }
                    />
                </div>
            </Dropdown>
        </div>
    )
})
